/**
 * WebOs Debug Utilities
 *
 * Système de logging unifié avec niveaux et couleurs.
 * Peut être désactivé en production.
 */

// Configuration globale
const DEBUG_CONFIG = {
  enabled: true,
  level: 'debug', // 'debug' | 'info' | 'warn' | 'error'
  showTimestamp: true,
  showSource: true
};

// Niveaux de log (ordre croissant de sévérité)
const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};

// Couleurs par source (pour la console)
const SOURCE_STYLES = {
  Kernel:    'background: #89b4fa; color: #1e1e2e; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
  SW:        'background: #a6e3a1; color: #1e1e2e; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
  FS:        'background: #f9e2af; color: #1e1e2e; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
  IPC:       'background: #cba6f7; color: #1e1e2e; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
  Process:   'background: #fab387; color: #1e1e2e; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
  Shell:     'background: #94e2d5; color: #1e1e2e; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
  App:       'background: #f5c2e7; color: #1e1e2e; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
  Gate:      'background: #f38ba8; color: #1e1e2e; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
  Default:   'background: #6c7086; color: #cdd6f4; padding: 2px 6px; border-radius: 3px;'
};

// Couleurs par niveau
const LEVEL_STYLES = {
  debug: 'color: #6c7086;',
  info:  'color: #cdd6f4;',
  warn:  'color: #f9e2af;',
  error: 'color: #f38ba8; font-weight: bold;'
};

/**
 * Crée un logger pour un composant spécifique
 * @param {string} source - Nom du composant (Kernel, SW, FS, etc.)
 * @returns {object} Logger avec méthodes debug, info, warn, error
 */
export function createLogger(source) {
  const sourceStyle = SOURCE_STYLES[source] || SOURCE_STYLES.Default;

  function log(level, ...args) {
    if (!DEBUG_CONFIG.enabled) return;
    if (LOG_LEVELS[level] < LOG_LEVELS[DEBUG_CONFIG.level]) return;

    const timestamp = DEBUG_CONFIG.showTimestamp
      ? new Date().toISOString().split('T')[1].slice(0, -1)
      : '';

    const prefix = [];
    const styles = [];

    if (DEBUG_CONFIG.showTimestamp) {
      prefix.push(`%c${timestamp}`);
      styles.push('color: #6c7086; font-size: 0.85em;');
    }

    if (DEBUG_CONFIG.showSource) {
      prefix.push(`%c${source}`);
      styles.push(sourceStyle);
    }

    prefix.push(`%c${level.toUpperCase()}`);
    styles.push(LEVEL_STYLES[level]);

    const consoleFn = level === 'error' ? console.error
                    : level === 'warn' ? console.warn
                    : console.log;

    consoleFn(prefix.join(' '), ...styles, ...args);
  }

  return {
    debug: (...args) => log('debug', ...args),
    info:  (...args) => log('info', ...args),
    warn:  (...args) => log('warn', ...args),
    error: (...args) => log('error', ...args),

    // Méthode pour logger un objet de manière détaillée
    inspect: (label, obj) => {
      log('debug', `${label}:`, obj);
    },

    // Méthode pour mesurer le temps d'exécution
    time: (label) => {
      const start = performance.now();
      return {
        end: () => {
          const duration = (performance.now() - start).toFixed(2);
          log('debug', `${label}: ${duration}ms`);
        }
      };
    },

    // Groupe de logs (collapse par défaut)
    group: (label, collapsed = true) => {
      if (!DEBUG_CONFIG.enabled) return { end: () => {} };

      if (collapsed) {
        console.groupCollapsed(`%c${source}%c ${label}`, sourceStyle, 'color: inherit;');
      } else {
        console.group(`%c${source}%c ${label}`, sourceStyle, 'color: inherit;');
      }

      return {
        end: () => console.groupEnd()
      };
    }
  };
}

/**
 * Configure le système de debug
 * @param {object} config - Configuration partielle
 */
export function configureDebug(config) {
  Object.assign(DEBUG_CONFIG, config);
}

/**
 * Désactive tout le debug (pour production)
 */
export function disableDebug() {
  DEBUG_CONFIG.enabled = false;
}

/**
 * Active le debug
 */
export function enableDebug() {
  DEBUG_CONFIG.enabled = true;
}

// Exposer la config pour debug dans la console
if (typeof window !== 'undefined') {
  window.__WEBOS_DEBUG__ = {
    config: DEBUG_CONFIG,
    configure: configureDebug,
    enable: enableDebug,
    disable: disableDebug
  };
}
